<template>
    <div class="activity-card" @click="onClick">
        <div class="left">
            <div
                class="avatar-wrapper"
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                :content="`Activity Actor is ${getActorName()}`"
            >
                <AgentAvatar v-if="activity.actor === 'user'" :size="50" :userID="$_.get(activity, 'actorUser._id')" />
                <Avatar
                    v-if="activity.actor === 'chatbot'"
                    :username="getActorName()"
                    class="avatar"
                    :src="baseUrl().concat('static/img/chatbot-bg.png')"
                />

                <Avatar
                    v-if="activity.actor === 'system'"
                    :username="getActorName()"
                    class="avatar"
                    :src="baseUrl().concat('static/img/minilogo4.png')"
                />
                <Avatar v-if="activity.actor === 'visitor'" :username="getActorName()" class="avatar" />

                <div class="icon">
                    <ActivityIconViewer :type="activity.type" />
                </div>
            </div>
            <div class="activity-content">
                <ActivityViewer :activity="activity" />
            </div>
        </div>
        <div class="right" v-if="showDate">
            <div
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                :content="$moment(activity.createdAt).format('DD/MM/YY hh:mm:ss')"
            >
                {{ $moment(activity.createdAt).fromNow() }}
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import ActivityViewer from "./cards/ActivityViewer.vue";
import ActivityTypes from "./ActivityTypes";
import _ from "lodash";
import Avatar from "vue-avatar";
import AgentAvatar from "../../../components/common/AgentAvatar.vue";
import ActivityIconViewer from "./cards/ActivityIconViewer.vue";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import { maskContactInfo, isMaskContactInfo, maskVisitorDisplayNameIfNeeded } from "../../../utils/utils";
import { mapState } from "vuex";

export default Vue.extend({
    name: "ActivityCard",
    components: { ActivityIconViewer, AgentAvatar, ActivityViewer, Avatar },
    props: {
        activity: {
            type: Object,
            default: () => ({}),
        },
        showDate: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            ActivityTypes,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
        }),
    },
    methods: {
        onClick() {
            const meta = Object.values(ActivityTypes).find((x) => x.id === this.activity.type);
            if (meta && meta.onClick) {
                meta.onClick(this.activity, this);
            }
        },
        executeAction(action) {
            // ExternalWidgetViewer.vue'nun mevcut message handling sistemini kullan
            if (action.action === "open-widget") {
                SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.OPEN_WIDGET, {
                    title: action.payload.title,
                    url: action.payload.url,
                    fullScreen: action.payload.fullScreen,
                    mode: action.payload.mode,
                    html: action.payload.html,
                });
            } else {
                window.postMessage(
                    {
                        type: action.action,
                        ...action.payload,
                    },
                    window.location.origin
                );
            }
        },
        getActorName() {
            if (this.activity.actor === "system") {
                return "System";
            } else if (this.activity.actor === "user") {
                return _.get(this.activity, "actorUser.name", "Agent");
            } else if (this.activity.actor === "admin-user") {
                return "System";
            } else if (this.activity.actor === "visitor") {
                const refVisitor = _.get(this.activity, "actorVisitor") || _.get(this.activity, "refVisitor");
                const nameRaw =
                    _.get(this.activity, "actorVisitor.fullname") || _.get(this.activity, "refVisitor.fullname");
                const name = maskVisitorDisplayNameIfNeeded(nameRaw, refVisitor);
                const email = _.get(this.activity, "actorVisitor.email") || _.get(this.activity, "refVisitor.email");
                const phone = _.get(this.activity, "actorVisitor.phone") || _.get(this.activity, "refVisitor.phone");
                return name || (isMaskContactInfo('email') ? maskContactInfo(email) : email) || (isMaskContactInfo('phone') ? maskContactInfo(phone) : phone) || "Visitor";
            } else if (this.activity.actor === "chatbot") {
                return "Chatbot";
            } else {
                return "Unknown";
            }
        },
        getActorAvatar() {
            if (this.activity.actor === "user") {
                return _.get(this.activity, "actorUser.name", "Agent");
            } else {
                return "Unknown";
            }
        },
        baseUrl: () => {
            if (document.currentScript) {
                let _documentUrl = document.currentScript.src.split("/");
                return _documentUrl[0] + "//" + _documentUrl[2] + "/";
            } else {
                return "/";
            }
        },
    },
});
</script>

<style lang="scss">
.activity-card {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid var(--color-border-default);
    background-color: var(--color-bg-default);
    padding: 20px;
    border-radius: 4px 4px 0 0;
    cursor: pointer;

    .tag {
        background-image: var(--color-bg-default);
        border: 1px solid var(--color-border-default);
        line-height: 20px;
        padding: 0 8px;
        border-radius: 4px;
        cursor: inherit;
        display: inline-block;
        color: var(--color-text-default);
        font-size: 14px;
        font-weight: 600;
        position: relative;
        margin-left: 5px;
    }

    .quote {
        padding-left: 8px;
        border-left: 2px solid var(--color-border-default);
        font-style: italic;
        line-height: 24px;
        display: block;
    }

    .left {
        display: flex;
        gap: 10px;
        align-items: center;

        .avatar-wrapper {
            display: flex;
            flex-direction: row;
            align-items: center;
            position: relative;

            .avatar {
                background-size: contain !important;
                padding: 4px !important;
            }

            #avatar-placeholder {
                width: auto !important;
            }

            .icon {
                display: flex;
                width: 24px;
                height: 24px;
                border-radius: 50%;
                position: absolute;
                align-content: center;
                align-items: center;
                justify-content: center;

                right: -6px;
                bottom: -4px;
                background-color: var(--color-bg-default);
                color: var(--color-icon-default);

                .MuiSvgIcon-root {
                    color: var(--color-icon-default);
                    width: 18px;
                    height: 18px;
                }

                svg.text-danger {
                    fill: var(--color-bg-critical-default) !important;
                }

                svg.text-success {
                    fill: var(--color-bg-success-default) !important;
                }
            }
        }
    }
}
</style>
